// const items = {{ cart.items | json }}
/**
 * @type {CartItem[]}
 */
// const items = [
//   {
//     id: "test1",
//     properties: {
//       size: 6,
//     },
//     url: "",
//     featured_image: {
//       url: "assets/suprise-box.jpg",
//       alt: "Suprise me",
//     },
//     final_price: 3100,
//     final_line_price: 3100,
//     title: "Surprise me - 6",
//     product_title: "Surprise me",
//     variant_title: "6",
//   },
//   {
//     id: "test2",
//     properties: {
//       groupId: 1,
//     },
//     final_line_price: 3600,
//     final_price: 600,
//     featured_image: {
//       url: "assets/biscoff.heic",
//       alt: "Biscoff dreams",
//     },
//     title: "Biscoff dreams",
//     quantity: 6,
//   },
// ];

/**
 * @typedef CartItem
 * @type {object}
 * @property {CartItemProperties | undefined} properties
 * @property {string} id
 * @property {string} title
 * @property {string} url
 * @property {CartItemImage | undefined} featured_image
 * @property {number} final_price
 * @property {number} final_line_price
 * @property {number} quantity
 * @property {string} variant_title
 * @property {string} product_title
 */

/**
 * @typedef CartItemExt
 * @type {object}
 * @extends {CartItem}
 * @property {string[] | undefined} parts
 */

/**
 * @typedef CartItemProperties
 * @type {object}
 * @property {string | undefined} groupId
 * @property {string | undefined} size
 */

/**
 * @typedef CartItemImage
 * @type {object}
 * @property {string} alt
 * @property {number} aspect_ratio
 * @property {string} url
 */

/**
 * @returns boolean
 */
function isLocalhost() {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

/**
 *
 * @param {CartItem[]} items
 *
 * @returns {{ individualItems: CartItem[]; groupedItems: CartItem[][]; }}
 */
const getProducts = (items) => {
  const individualItems = [];
  const groupedItemsMap = {};

  items.forEach((i) => {
    if (i.properties.groupId) {
      if (!groupedItemsMap[i.properties?.groupId]) {
        groupedItemsMap[i.properties?.groupId] = [i];
      } else {
        groupedItemsMap[i.properties?.groupId].push(i);
      }
    } else {
      individualItems.push(i);
    }
  });

  return { individualItems, groupedItems: Object.values(groupedItemsMap) };
};

/**
 *
 * @param {CartItemExt} product
 * @param {HTMLElement} cartItemsContainer
 * @param {HTMLElement} removeIconOriginal
 * @param {() => Promise<void>} onRemove
 */
const displayProduct = (product, cartItemsContainer, removeIconOriginal, onRemove) => {
  const cartItem = document.createElement('div');
  cartItem.classList.add(ElementClassNames.CartItem);

  const imageContainer = document.createElement('div');
  imageContainer.classList.add(ElementClassNames.CartItemImage);

  const image = document.createElement('img');
  image.setAttribute('src', product.featured_image.url);
  image.setAttribute('alt', product.featured_image.alt);

  imageContainer.append(image);

  cartItem.append(imageContainer);

  const cartItemContents = document.createElement('div');
  cartItemContents.classList.add(ElementClassNames.CartItemContents);

  const header = document.createElement('div');
  header.classList.add(ElementClassNames.CartItemHeader);

  const title = document.createElement('h3');
  title.classList.add(ElementClassNames.CartItemTitle);
  title.textContent = product.title;

  header.append(title);

  const size = document.createElement('p');
  size.classList.add(ElementClassNames.CartItemSize);
  if (product.properties.size) {
    size.textContent = `Size: ${product.properties.size}`;
  }

  header.append(size);

  if (product.parts && product.parts.length) {
    const description = document.createElement('div');
    description.classList.add(ElementClassNames.CartItemDescription);

    product.parts.forEach((part) => {
      const partElement = document.createElement('p');
      partElement.textContent = part;
      description.append(partElement);
    });

    header.append(description);
  }

  cartItemContents.append(header);

  const total = document.createElement('p');
  total.classList.add(ElementClassNames.CartItemTotal);
  total.textContent = `$${(product.final_price / 100).toFixed(2)}`;

  cartItemContents.append(total);

  cartItem.append(cartItemContents);

  const remove = document.createElement('button');
  remove.setAttribute('type', 'button');
  remove.classList.add(ElementClassNames.CartItemRemove);

  const removeIcon = removeIconOriginal.cloneNode(true);

  remove.addEventListener('click', async () => {
    let removeBtns = Array.from(document.getElementsByClassName(ElementClassNames.CartItemRemove));

    removeBtns.forEach((btn) => (btn.disabled = true));

    try {
      await onRemove();
      cartItemsContainer.removeChild(cartItem);
    } catch (e) {
      console.error(e.message);
    }
    removeBtns = Array.from(document.getElementsByClassName(ElementClassNames.CartItemRemove));
    removeBtns.forEach((btn) => (btn.disabled = false));
  });

  remove.append(removeIcon);

  cartItem.append(remove);

  cartItemsContainer.append(cartItem);
};

/**
 *
 * @param {CartItem | CartItem[]} cartItem
 */
const updateCart = async (cartItem) => {
  const body = {};

  if (Array.isArray(cartItem)) {
    cartItem.forEach((item) => {
      body[item.id] = item.quantity;
    });
  } else {
    body[cartItem.id] = cartItem.quantity;
  }

  const response = await fetch(`${routes.cart_update_url}`, {
    ...fetchConfig(),
    ...{ body: JSON.stringify({ updates: body }) },
  });

  return response;
};

/**
 *
 * @param {CartItem | CartItem[]} cartItem
 */
const removeItem = async (cartItem) => {
  if (Array.isArray(cartItem)) {
    return await updateCart(cartItem.map((item) => ({ ...item, quantity: 0 })));
  } else {
    return await updateCart({ ...cartItem, quantity: 0 });
  }
};

const ElementIds = {
  CartItems: 'cart-items',
};

const ElementClassNames = {
  CartItem: 'cart-item',
  CartItemContents: 'cart-item-contents',
  CartItemDescription: 'cart-item_description',
  CartItemHeader: 'cart-item_header',
  CartItemImage: 'cart-item_image',
  CartItemSize: 'cart-item_size',
  CartItemRemove: 'cart-item_remove',
  CartItemTitle: 'cart-item_title',
  CartItemTotal: 'cart-item_total',
  RemoveIcon: 'remove-icon',
};

document.addEventListener('DOMContentLoaded', () => {
  const cartItems = document.getElementById(ElementIds.CartItems);
  const removeIconOriginal = Array.from(document.getElementsByClassName(ElementClassNames.RemoveIcon))[0];

  const { groupedItems, individualItems } = getProducts(items);

  for (const item of individualItems) {
    const onRemove = async () => {
      const response = await removeItem(item);

      if (!response.ok) {
        const warningContainer = document.getElementsByClassName('cart__warnings')[0];
        const error = document.createTextNode(`Error: ${JSON.stringify((await response.json()).errors)}`);
        warningContainer.appendChild(error);
      } else {
        const warningContainer = document.getElementsByClassName('cart__warnings')[0];
        warningContainer.innerHTML = '';

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: await response.json(),
        });

        // TODO: replace with section renderer API
        window.location.reload();
      }
    };
    /**
     * @type {CartItemExt}
     */
    const finalProduct = {
      ...item,
      title: item.product_title,
      properties: {
        size: item.variant_title,
      },
    };
    displayProduct(finalProduct, cartItems, removeIconOriginal, onRemove);
  }

  for (const items of groupedItems) {
    let finalPrice = 0;
    let size = 0;
    const parts = [];

    items.forEach((item) => {
      finalPrice += item.final_line_price;
      parts.push(`${item.title} x ${item.quantity} @ $${(item.final_price / 100).toFixed(2)}/ea`);
      size += item.quantity;
    });

    const productEntries = Object.entries(productsMap);
    const collectionId = productEntries.find(([id, products]) => {
      return products.find((p) => {
        return p.variants.find((v) => v.id.toString() === items[0].id.toString());
      });
    })[0];

    const collection = collections.find((c) => c.id.toString() === collectionId.toString());

    /**
     * @type {CartItemExt}
     */
    const finalItem = {
      final_price: finalPrice,
      featured_image: items[0].featured_image,
      parts,
      properties: {
        size,
      },
      title: collection.title,
    };

    const onRemove = async () => {
      const response = await removeItem(items);

      if (!response.ok) {
        const warningContainer = document.getElementsByClassName('cart__warnings')[0];
        const error = document.createTextNode(`Error: ${JSON.stringify((await response.json()).errors)}`);
        warningContainer.appendChild(error);
      } else {
        const warningContainer = document.getElementsByClassName('cart__warnings')[0];
        warningContainer.innerHTML = '';

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: await response.json(),
        });

        // TODO: replace with section renderer API
        window.location.reload();
      }
    };

    // display grouped items
    displayProduct(finalItem, cartItems, removeIconOriginal, onRemove);
  }
});

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({
              note: event.target.value,
            });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{
                body,
              },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
